import { Button } from "@msuite/ui/button";
import { Icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { ConfirmModal, useAsyncModal } from "@msuite/ui/modal";
import { invalidate, useQueryClient } from "@msuite/ui/query";
import { toast } from "@msuite/ui/toast";
import { Tooltip } from "@msuite/ui/tooltip";
import { responseHandler } from "@msuite/ui/utils";
import React from "react";
import { PaymentFileDownloadButton } from "@/components/incoming-invoices";
import { client } from "@/hono";
import { useCitadel } from "@/hooks/use-citadel";

/** Props Interface */
interface IncomingInvoicePaymentActionsProps {
	incomingInvoiceId: string;
	paymentRunId: string;
	/** Whether the payment went out as a transfer; one settled entirely by offset has no file line. */
	hasTransfer: boolean;
}

/**
 * Download the payment file again, and take the payment back. Both need `update
 * IncomingInvoice`, which the routes ask for — a caller without it sees neither.
 */
export function IncomingInvoicePaymentActions({
	incomingInvoiceId,
	paymentRunId,
	hasTransfer,
}: IncomingInvoicePaymentActionsProps) {
	/** Context */
	const { t } = useTranslation();

	/** Hooks */
	const { citadel } = useCitadel();
	const qc = useQueryClient();
	const confirmModal = useAsyncModal();

	/** Functions */
	async function handleOnRevert() {
		if (!(await confirmModal.confirm())) return;
		const response = await client.incoming_invoices[":incomingInvoiceId"].payment.$delete({
			param: { incomingInvoiceId },
		});
		await responseHandler(response);
		toast.success(t("incoming_invoices.payments.payment_reverted"));
		await qc.invalidateQueries({ predicate: invalidate(["incoming-invoices"]) });
		// Taking the payment back opens the outgoing invoices offset against it again.
		await qc.invalidateQueries({ predicate: invalidate(["invoices"]) });
	}

	/** Guard */
	if (!citadel || citadel.cannot("update", "IncomingInvoice")) return null;

	/** Render */
	return (
		<React.Fragment>
			<div className="h-stack gap-1">
				<PaymentFileDownloadButton
					paymentRunId={paymentRunId}
					disabledReason={
						hasTransfer ? null : t("incoming_invoices.payments.offsets.paid_by_offset")
					}
				/>
				<Tooltip label={t("incoming_invoices.payments.revert_payment")}>
					<Button
						size="xs"
						variant="ghost"
						icon={<Icon name="Undo2" />}
						onClick={handleOnRevert}
						data-cy="revert-incoming-invoice-payment-button"
					/>
				</Tooltip>
			</div>
			{confirmModal.isOpen && (
				<ConfirmModal
					title={t("incoming_invoices.payments.revert_payment")}
					message={t("incoming_invoices.payments.revert_payment_message")}
					confirmLabel={t("incoming_invoices.payments.revert_payment")}
					confirmVariant="danger"
					controller={confirmModal}
				/>
			)}
		</React.Fragment>
	);
}
